import {createSortable} from '../../../../js/modules/sortable.ts';

function widgetOrder(el: Element): number {
  return Number(el.getAttribute('data-widget-order') || Number.MAX_SAFE_INTEGER);
}

function insertByOrder(container: HTMLElement, card: HTMLElement) {
  const siblings = Array.from(container.querySelectorAll<HTMLElement>('.devops-layout-card')).filter((el) => el !== card);
  const nextSibling = siblings.find((el) => widgetOrder(el) > widgetOrder(card));
  if (nextSibling) {
    container.insertBefore(card, nextSibling);
    return;
  }
  container.append(card);
}

export async function initAdminDevopsDashboardConfig() {
  // Show/hide "selected subjects" rows when the global access mode changes.
  // Must run before the early return so the select listener is always registered.
  //
  // Root cause note: the <select> itself carries class="ui dropdown", so
  // closest('.ui.dropdown') returns the select element itself, not the Fomantic
  // wrapper div.  Listening for clicks on the hidden <select> would never fire.
  // Fomantic DOES dispatch a native 'change' event on the underlying <select>
  // after updating its value, so we only need a plain change listener here.
  const accessModeSelect = document.querySelector<HTMLSelectElement>('select[name="ee.dashboard.devops.access_mode"]');
  const selectedSubjectRows = document.querySelectorAll<HTMLElement>('.js-devops-dashboard-selected-subjects');
  if (accessModeSelect && selectedSubjectRows.length) {
    const syncSelectedSubjectRows = () => {
      const isSelected = accessModeSelect.value === 'selected_subjects';
      for (const row of selectedSubjectRows) {
        row.hidden = !isSelected;
      }
    };
    accessModeSelect.addEventListener('change', syncSelectedSubjectRows);
    syncSelectedSubjectRows();
  }

  const root = document.querySelector<HTMLElement>('#devops-dashboard-layout-editor');
  if (!root) return;

  const availableList = root.querySelector<HTMLElement>('[data-role="available"]');
  const previewList = root.querySelector<HTMLElement>('[data-role="preview"]');
  const layoutInput = root.querySelector<HTMLInputElement>('[data-role="layout-input"]');
  if (!availableList || !previewList || !layoutInput) return;

  const dragFilter = '[data-action="remove"], [data-action="size"], button, a, input, select, textarea';

  const defaultLayout = JSON.parse(root.getAttribute('data-default-layout') || '[]') as Array<{id: string; size: string}>;
  const tooltip = document.createElement('div');
  tooltip.className = 'devops-layout-tooltip';
  tooltip.hidden = true;
  const tooltipArrow = document.createElement('span');
  tooltipArrow.className = 'devops-layout-tooltip-arrow';
  tooltipArrow.setAttribute('aria-hidden', 'true');
  const tooltipContentEl = document.createElement('span');
  tooltipContentEl.className = 'devops-layout-tooltip-content';
  tooltip.append(tooltipArrow, tooltipContentEl);
  document.body.append(tooltip);

  let tooltipTarget: HTMLElement | null = null;

  const hideTooltip = () => {
    tooltip.hidden = true;
    tooltipTarget = null;
  };

  const positionTooltip = (target: HTMLElement, mouseEvent?: MouseEvent) => {
    const offset = 16;
    if (mouseEvent) {
      tooltip.style.left = `${Math.min(mouseEvent.clientX + offset, window.innerWidth - 320)}px`;
      tooltip.style.top = `${Math.min(mouseEvent.clientY + offset, window.innerHeight - 120)}px`;
      return;
    }

    const rect = target.getBoundingClientRect();
    tooltip.style.left = `${Math.min(rect.left, window.innerWidth - 320)}px`;
    tooltip.style.top = `${Math.min(rect.bottom + 8, window.innerHeight - 120)}px`;
  };

  const showTooltip = (target: HTMLElement, mouseEvent?: MouseEvent) => {
    const text = target.getAttribute('data-tooltip-text')?.trim();
    if (!text) return;
    tooltipContentEl.textContent = text;
    tooltip.hidden = false;
    tooltipTarget = target;
    positionTooltip(target, mouseEvent);
  };

  const syncLayout = () => {
    layoutInput.value = JSON.stringify(Array.from(previewList.querySelectorAll<HTMLElement>('.devops-layout-card'), (el) => ({
      id: el.getAttribute('data-widget-id') || '',
      size: el.getAttribute('data-widget-span') || '1x1',
    })).filter((item) => item.id));
  };

  const updateSizeControls = (card: HTMLElement) => {
    const currentSize = card.getAttribute('data-widget-span') || '1x1';
    const badge = card.querySelector<HTMLElement>('.devops-layout-size-badge');
    if (badge) badge.textContent = currentSize;
    for (const button of card.querySelectorAll<HTMLButtonElement>('[data-action="size"]')) {
      button.classList.toggle('is-active', button.getAttribute('data-size') === currentSize);
    }
  };

  const setDragState = (isDragging: boolean) => {
    previewList.classList.toggle('is-drop-active', isDragging);
    if (isDragging) hideTooltip();
  };

  root.addEventListener('mouseover', (event) => {
    const title = (event.target as Element)?.closest<HTMLElement>('.devops-layout-card-title');
    if (!title) return;
    showTooltip(title, event);
  });

  root.addEventListener('mousemove', (event) => {
    if (!tooltipTarget) return;
    positionTooltip(tooltipTarget, event);
  });

  root.addEventListener('mouseout', (event) => {
    const title = (event.target as Element)?.closest<HTMLElement>('.devops-layout-card-title');
    if (!title) return;
    const related = event.relatedTarget as Node | null;
    if (related && title.contains(related)) return;
    hideTooltip();
  });

  root.addEventListener('focusin', (event) => {
    const title = (event.target as Element)?.closest<HTMLElement>('.devops-layout-card-title');
    if (!title) return;
    showTooltip(title);
  });

  root.addEventListener('focusout', (event) => {
    const title = (event.target as Element)?.closest<HTMLElement>('.devops-layout-card-title');
    if (!title) return;
    const related = event.relatedTarget as Node | null;
    if (related && title.contains(related)) return;
    hideTooltip();
  });

  root.addEventListener('click', (event) => {
    const removeButton = (event.target as Element)?.closest<HTMLButtonElement>('[data-action="remove"]');
    if (removeButton) {
      const card = removeButton.closest<HTMLElement>('.devops-layout-card');
      if (!card || !previewList.contains(card)) return;

      insertByOrder(availableList, card);
      syncLayout();
      return;
    }

    const restoreButton = (event.target as Element)?.closest<HTMLButtonElement>('[data-action="restore-defaults"]');
    if (restoreButton) {
      const confirmMessage = restoreButton.getAttribute('data-confirm-message') || '';
      if (confirmMessage && !window.confirm(confirmMessage)) return;

      const defaultLayoutMap = new Map(defaultLayout.map((item) => [item.id, item.size] as const));
      const defaultVisible = new Set(defaultLayout.map((item) => item.id));
      for (const card of root.querySelectorAll<HTMLElement>('.devops-layout-card')) {
        const widgetID = card.getAttribute('data-widget-id') || '';
        card.setAttribute('data-widget-span', defaultLayoutMap.get(widgetID) || '1x1');
        updateSizeControls(card);
        if (defaultVisible.has(widgetID)) {
          insertByOrder(previewList, card);
        } else {
          insertByOrder(availableList, card);
        }
      }
      syncLayout();
      return;
    }

    const sizeButton = (event.target as Element)?.closest<HTMLButtonElement>('[data-action="size"]');
    if (!sizeButton) return;
    const card = sizeButton.closest<HTMLElement>('.devops-layout-card');
    if (!card || !previewList.contains(card)) return;

    card.setAttribute('data-widget-span', sizeButton.getAttribute('data-size') || '1x1');
    updateSizeControls(card);
    syncLayout();
  });

  for (const card of root.querySelectorAll<HTMLElement>('.devops-layout-card')) {
    updateSizeControls(card);
  }

  await createSortable(availableList, {
    group: {
      name: 'devops-dashboard-widgets',
      pull: true,
      put: false,
    },
    sort: false,
    filter: dragFilter,
    preventOnFilter: false,
    onChoose: () => {
      setDragState(true);
    },
    onEnd: () => {
      setDragState(false);
      syncLayout();
    },
  });

  await createSortable(previewList, {
    group: {
      name: 'devops-dashboard-widgets',
      pull: true,
      put: true,
    },
    filter: dragFilter,
    preventOnFilter: false,
    onChoose: () => {
      setDragState(true);
    },
    onAdd: () => {
      syncLayout();
    },
    onEnd: () => {
      setDragState(false);
      syncLayout();
    },
    onUnchoose: () => {
      setDragState(false);
    },
  });

  syncLayout();
}
